import { useState } from 'react';
import MediaUploader, { kindFromMime } from '../../components/MediaUploader';
import VideoEmbedPreview from '../../components/VideoEmbedPreview';
import RichTextEditor from '../../components/RichTextEditor';
import { renderRichText } from '../../utils/richText';

// Only 2 shipped block types in v1, so - unlike exercise-templates' one-
// file-per-type split - editors + registry + the config-parse helper all
// live together here. Still registry-driven (TopicElementCard looks up by
// `type`, falls back to GenericBlockEditor) so a 3rd type later only means
// adding an entry below, per topicBlockTypes.js's own doc comment.

function safeParseConfig(configString, fallback) {
    if (!configString) return fallback;
    try {
        const parsed = JSON.parse(configString);
        return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch (e) {
        return fallback;
    }
}

export function TextBlockEditor({ block, onSave, onCancel }) {
    const [text, setText] = useState(block.text || '');
    return (
        <div className='exercise-block-generic-editor'>
            <RichTextEditor value={text} onChange={setText} rows={4}
                placeholder='Текст. Виділіть слово, щоб оформити (жирний/курсив/підкреслення/закреслення/маркер)' />
            <div className='exercise-block-editor-actions'>
                <button type='button' className='exercise-block-save-btn' onClick={() => onSave({ text })}>Зберегти</button>
                <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
            </div>
        </div>
    );
}

export function renderTextPreview(block) {
    return block.text
        ? <div className='exercise-block-text-preview'>{renderRichText(block.text)}</div>
        : <span className='exercise-block-empty-hint'>Порожньо - натисніть редагувати</span>;
}

const MEDIA_DEFAULT = { url: '', caption: '', kind: null, fileId: null };

// Real upload/record (MediaUploader, shared with exercise-templates' own
// Media editor) alongside the original pasted-URL field - kept, not
// replaced, since an author may still want to link an external asset
// rather than upload one. `kind` (image/audio/video/pdf, from the upload
// response's detected MIME) gets stored alongside `url` so the preview
// below doesn't have to keep guessing from a file extension - `courseId`
// is required for upload (threaded down from TopicWorkspace.jsx; MediaUploader
// itself shows an explanatory line in its place when absent, this editor
// doesn't need its own fallback branch for that).
export function MediaBlockEditor({ block, courseId, onSave, onCancel }) {
    const initial = safeParseConfig(block.config, MEDIA_DEFAULT);
    const [url, setUrl] = useState(initial.url || '');
    const [caption, setCaption] = useState(initial.caption || '');
    const [kind, setKind] = useState(initial.kind || null);
    const [fileId, setFileId] = useState(initial.fileId || null);

    const handleSave = () => onSave({ config: JSON.stringify({ url, caption, kind, fileId }) });

    const handleUploaded = (uploadedUrl, fileMeta) => {
        setUrl(uploadedUrl);
        setKind(kindFromMime(fileMeta.type));
        setFileId(fileMeta.id);
    };

    return (
        <div className='exercise-block-media-editor'>
            <MediaUploader courseId={courseId} kind={null} onUploaded={handleUploaded} />
            <div className='exercise-block-or-divider'><span>або вставте посилання</span></div>
            <div className='exercise-block-field'>
                <label className='exercise-block-field-label'>URL файлу</label>
                <input className='exercise-block-generic-text' value={url}
                    placeholder='https://...'
                    onChange={e => { setUrl(e.target.value); setKind(null); setFileId(null); }} />
            </div>
            <div className='exercise-block-field'>
                <label className='exercise-block-field-label'>Підпис</label>
                <input className='exercise-block-generic-text' value={caption} placeholder='Необовʼязково' onChange={e => setCaption(e.target.value)} />
            </div>
            <div className='exercise-block-editor-actions'>
                <button type='button' className='exercise-block-save-btn' onClick={handleSave}>Зберегти</button>
                <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
            </div>
        </div>
    );
}

export function renderMediaPreview(block) {
    const cfg = safeParseConfig(block.config, MEDIA_DEFAULT);
    if (!cfg.url) return <span className='exercise-block-empty-hint'>Порожньо</span>;
    const kind = cfg.kind || (/\.(png|jpe?g|gif|webp)$/i.test(cfg.url) ? 'image'
        : /\.(mp3|wav|ogg)$/i.test(cfg.url) ? 'audio'
        : /\.(mp4|webm)$/i.test(cfg.url) ? 'video' : null);
    let media;
    if (kind === 'image') media = <img className='exercise-block-media-image' src={cfg.url} alt='' />;
    else if (kind === 'audio') media = <audio className='exercise-block-media-audio' src={cfg.url} controls />;
    else if (kind === 'video') media = <video className='exercise-block-media-video' src={cfg.url} controls />;
    else media = <a href={cfg.url} target='_blank' rel='noreferrer'>{cfg.url}</a>;
    return (
        <div className='exercise-block-media-preview'>
            {cfg.caption ? <p className='exercise-block-media-caption'>{cfg.caption}</p> : ''}
            {media}
            
        </div>
    );
}

const VIDEO_EMBED_DEFAULT = { url: '', description: '' };

// Separate from MediaBlockEditor on purpose (user's explicit ask) - this is
// only ever a pasted external link (YouTube/Vimeo), never an upload, so no
// MediaUploader here. Live VideoEmbedPreview right in the editor so the
// author sees immediately whether the pasted link actually resolves before
// saving, instead of finding out on the course-preview page.
export function VideoEmbedBlockEditor({ block, onSave, onCancel }) {
    const initial = safeParseConfig(block.config, VIDEO_EMBED_DEFAULT);
    const [url, setUrl] = useState(initial.url || '');
    const [description, setDescription] = useState(initial.description || '');

    const handleSave = () => onSave({ config: JSON.stringify({ url, description }) });

    return (
        <div className='exercise-block-media-editor'>
            <div className='exercise-block-field'>
                <label className='exercise-block-field-label'>Посилання на відео</label>
                <input className='exercise-block-generic-text' value={url}
                    placeholder='https://www.youtube.com/watch?v=...'
                    onChange={e => setUrl(e.target.value)} />
            </div>
            <div className='exercise-block-field'>
                <label className='exercise-block-field-label'>Опис</label>
                <input className='exercise-block-generic-text' value={description}
                    placeholder='Необовʼязково' onChange={e => setDescription(e.target.value)} />
            </div>
            <VideoEmbedPreview url={url} description={description} />
            <div className='exercise-block-editor-actions'>
                <button type='button' className='exercise-block-save-btn' onClick={handleSave}>Зберегти</button>
                <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
            </div>
        </div>
    );
}

export function renderVideoEmbedPreview(block) {
    const cfg = safeParseConfig(block.config, VIDEO_EMBED_DEFAULT);
    return <VideoEmbedPreview url={cfg.url} description={cfg.description} />;
}

export function GenericBlockEditor({ block, onSave, onCancel }) {
    const [text, setText] = useState(block.text || '');
    const [configText, setConfigText] = useState(block.config || '');
    return (
        <div className='exercise-block-generic-editor'>
            <textarea className='exercise-block-generic-text' rows={2} value={text}
                placeholder='Текст' onChange={e => setText(e.target.value)} />
            <textarea className='exercise-block-generic-config' rows={3} value={configText}
                placeholder='config (JSON)' onChange={e => setConfigText(e.target.value)} />
            <div className='exercise-block-editor-actions'>
                <button type='button' className='exercise-block-save-btn' onClick={() => onSave({ text, config: configText })}>Зберегти</button>
                <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
            </div>
        </div>
    );
}

export function renderGenericPreview(block) {
    return block.text
        ? <p>{block.text}</p>
        : <span className='exercise-block-empty-hint'>Порожньо - натисніть редагувати</span>;
}

export const TOPIC_BLOCK_EDITOR_REGISTRY = {
    text: { Editor: TextBlockEditor, renderPreview: renderTextPreview },
    media: { Editor: MediaBlockEditor, renderPreview: renderMediaPreview },
    video_embed: { Editor: VideoEmbedBlockEditor, renderPreview: renderVideoEmbedPreview },
};
